import type { IVariantIntervalSeries } from '../hooks/useFetchCampaignAnalyticDetailV2';
import type { AnalyticInterval } from '~/modules/analytics/types';
import type { IBaseCampaign } from '~/modules/campaign/types';
import type { IAnalyticMetric } from '~/types/metric';

import { useFetchCampaignAnalyticDetailV2 } from '../hooks/useFetchCampaignAnalyticDetailV2';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useFetchCampaignAnalyticsV2 } from '../hooks/useFetchCampaignAnalyticsV2';
import { shouldQueryPreviousPeriod } from '../helpers/shouldQueryPreviousPeriod';
import { DEFAULT_ANALYTIC_DATA_A, DEFAULT_ANALYTIC_DATA_B } from '../constants';
import { findVariantByType } from '~/modules/campaign/utils';
import { useMetricsTimeFilter } from '~/modules/analytics';
import { EAnalyticMode } from '~/modules/analytics/types';
import { IVariantType } from '~/modules/campaign/types';
import { CAMPAIGN_STATUS } from '~/constants/campaign';

export interface CampaignAnalyticContextType {
  metrics: IAnalyticMetric[];
  metricsPrevious: IAnalyticMetric[];
  analyticMode: EAnalyticMode;
  isEmptyMetricData: boolean;
  isLoadingMetric: boolean;
  handleSetAnalyticMode: (mode: EAnalyticMode) => void;
  /** Populated only when isAnalyticDetail=true. Empty array otherwise. */
  metricInterval: IVariantIntervalSeries[];
  /** Populated only when isAnalyticDetail=true. */
  interval?: AnalyticInterval;
  /** Populated only when isAnalyticDetail=true. False otherwise. */
  isLoadingMetricInterval: boolean;
}
export interface CampaignAnalyticProviderProps {
  children: React.ReactNode;
  campaign?: IBaseCampaign;
  isQueryPreviousData: boolean;
  analyticMode?: EAnalyticMode;
  /**
   * When true, fires a single per-variant TIMESERIES+TOTALS query per period so the listing
   * (totals) AND chart (interval) data come from the same response. Default false keeps
   * list/widget mounts on the cheap listing-only query shape.
   */
  isAnalyticDetail?: boolean;
}

export const AnalyticCampaignContext = createContext<CampaignAnalyticContextType | undefined>(
  undefined,
);

export const AnalyticCampaignProvider: React.FC<CampaignAnalyticProviderProps> = ({
  children,
  campaign,
  isQueryPreviousData,
  isAnalyticDetail = false,
  ...props
}) => {
  const { currentDates, previousDates } = useMetricsTimeFilter();

  const [analyticMode, setAnalyticMode] = useState<EAnalyticMode>(EAnalyticMode.ALL_SESSION);

  const versions = campaign?.versions;
  // Drafts have variant rows in the DB (non-empty IDs) but no traffic — queries would return
  // empty data. Skip them entirely to avoid wasted requests.
  const isDraftCampaign = campaign?.status === CAMPAIGN_STATUS.DRAFT;
  const campaignVariantIdA = isDraftCampaign
    ? ''
    : findVariantByType(campaign, IVariantType.VARIANT_A)?.id || '';
  const campaignVariantIdB = isDraftCampaign
    ? ''
    : findVariantByType(campaign, IVariantType.VARIANT_B)?.id || '';

  const effectiveIsQueryPreviousData = useMemo(
    () =>
      shouldQueryPreviousPeriod({
        isQueryPreviousData,
        versions,
        previousEndDate: previousDates.endDate,
      }),
    [isQueryPreviousData, versions, previousDates.endDate],
  );

  // Matched on the campaign's own versions, not on `currentDates.versionId`: inside a group page
  // the picked range carries the GROUP version id, which would filter out every campaign row.
  const campaignVersionID = useMemo(
    () => versions?.find((version) => version?.version === currentDates.alias)?.id,
    [versions, currentDates.alias],
  );

  const listingResult = useFetchCampaignAnalyticsV2({
    campaignVariantIdA: isAnalyticDetail ? '' : campaignVariantIdA,
    campaignVariantIdB: isAnalyticDetail ? '' : campaignVariantIdB,
    campaignVersionID,
    currentDates,
    previousDates,
    isQueryPreviousData: effectiveIsQueryPreviousData,
    analyticMode,
  });

  const detailVariantIdA = isAnalyticDetail ? campaignVariantIdA : '';
  const detailVariantIdB = isAnalyticDetail ? campaignVariantIdB : '';
  const detailResult = useFetchCampaignAnalyticDetailV2({
    campaignVariantIdA: detailVariantIdA,
    campaignVariantIdB: detailVariantIdB,
    campaignVersionID,
    currentDates,
    previousDates,
    isQueryPreviousData: effectiveIsQueryPreviousData,
    analyticMode,
  });

  const currentAnalyticA = isAnalyticDetail
    ? detailResult.campaignAnalytic[0]
    : listingResult.campaignAnalyticA?.[0];
  const currentAnalyticB = isAnalyticDetail
    ? detailResult.campaignAnalytic[1]
    : listingResult.campaignAnalyticB?.[0];
  const previousAnalyticA = isAnalyticDetail
    ? detailResult.previousCampaignAnalytic[0]
    : listingResult.previousCampaignAnalyticA?.[0];
  const previousAnalyticB = isAnalyticDetail
    ? detailResult.previousCampaignAnalytic[1]
    : listingResult.previousCampaignAnalyticB?.[0];

  const metrics = useMemo(() => {
    const metricA = currentAnalyticA?.metrics[0];
    const metricB = currentAnalyticB?.metrics[0];

    return [
      { ...(metricA || DEFAULT_ANALYTIC_DATA_A), variantKey: IVariantType.VARIANT_A },
      { ...(metricB || DEFAULT_ANALYTIC_DATA_B), variantKey: IVariantType.VARIANT_B },
    ];
  }, [currentAnalyticA, currentAnalyticB]);

  const metricsPrevious = useMemo(() => {
    const metricA = previousAnalyticA?.metrics[0];
    const metricB = previousAnalyticB?.metrics[0];

    return [
      { ...(metricA || DEFAULT_ANALYTIC_DATA_A), variantKey: IVariantType.VARIANT_A },
      { ...(metricB || DEFAULT_ANALYTIC_DATA_B), variantKey: IVariantType.VARIANT_B },
    ];
  }, [previousAnalyticA, previousAnalyticB]);

  const isEmptyMetricData = useMemo(() => {
    const metricA = currentAnalyticA?.metrics[0];
    const metricB = currentAnalyticB?.metrics[0];

    return (metricA?.totalPageViewed || 0) === 0 && (metricB?.totalPageViewed || 0) === 0;
  }, [currentAnalyticA, currentAnalyticB]);

  const isLoadingMetric = isAnalyticDetail ? detailResult.isLoadingMetric : listingResult.isLoading;

  const handleSetAnalyticMode = useCallback((mode: EAnalyticMode) => {
    setAnalyticMode(mode);
  }, []);

  useEffect(() => {
    if (!props.analyticMode) return;

    setAnalyticMode(props.analyticMode);
  }, [props.analyticMode]);

  const contextValue = useMemo<CampaignAnalyticContextType>(
    () => ({
      metrics,
      metricsPrevious,
      analyticMode,
      isEmptyMetricData,
      isLoadingMetric,
      handleSetAnalyticMode,
      metricInterval: isAnalyticDetail ? detailResult.metricInterval : [],
      interval: isAnalyticDetail ? detailResult.interval : undefined,
      isLoadingMetricInterval: isAnalyticDetail ? detailResult.isLoadingMetricInterval : false,
    }),
    [
      metrics,
      metricsPrevious,
      analyticMode,
      isEmptyMetricData,
      isLoadingMetric,
      handleSetAnalyticMode,
      isAnalyticDetail,
      detailResult.metricInterval,
      detailResult.interval,
      detailResult.isLoadingMetricInterval,
    ],
  );

  return (
    <AnalyticCampaignContext.Provider value={contextValue}>
      {children}
    </AnalyticCampaignContext.Provider>
  );
};

export const useCampaignAnalyticContext = () => {
  const context = useContext(AnalyticCampaignContext);
  if (!context) {
    throw new Error('useCampaignAnalyticContext must be used within a AnalyticCampaignProvider');
  }
  return context;
};
